// Signposts: a forecast's triggers as rows on one shared time axis (today → resolution), so the
// reader can see, without hovering, when each one could happen, how likely it is, and how far it
// would move the forecast. Pure: no React, no DOM.
import { eventKey } from "../forecast/eventIdentity.js";

const DAY = 86400000;

const isProb = (v) => typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 1;

/** A trigger date ("YYYY-MM-DD" or ISO) as epoch ms; a bare date is local midnight. NaN if unusable. */
export function parseTriggerDate(v) {
  if (typeof v !== "string" || !v.trim()) return NaN;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v.trim());
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
  const t = Date.parse(v);
  return Number.isFinite(t) ? t : NaN;
}

/** "Dec 15, 2026". */
export function fmtSignpostDate(ms) {
  if (!Number.isFinite(ms)) return "";
  return new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** "Jan 12 – Jan 18, 2027", or "Dec 15, 2026 – Jan 3, 2027" across a year boundary. */
function fmtRange(startMs, endMs) {
  const a = new Date(startMs);
  const b = new Date(endMs);
  if (a.getFullYear() === b.getFullYear()) {
    const head = a.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    return `${head} – ${fmtSignpostDate(endMs)}`;
  }
  return `${fmtSignpostDate(startMs)} – ${fmtSignpostDate(endMs)}`;
}

/**
 * P(the question's event happens | this trigger occurs) for a yes/no question, or null.
 * `a` is the scorer's direct answer; otherwise `p_conditional` is the chance of the trigger's
 * own `direction` side, so a "no" trigger's figure is flipped onto the Yes side.
 */
export function ifOccursProbability(ev) {
  if (isProb(ev?.a)) return ev.a;
  if (isProb(ev?.p_conditional)) {
    return ev.direction === "no" ? 1 - ev.p_conditional : ev.p_conditional;
  }
  return null;
}

/**
 * How far this trigger would move the forecast if it happened, in percentage points (0–100),
 * or null when the data does not say. Multi-outcome questions carry `dist_impact` (the total
 * variation distance the trigger moves the distribution); yes/no questions compare the
 * if-occurs probability with the current forecast (or with the if-not probability `b`).
 */
export function signpostShift(ev, probability) {
  if (typeof ev?.dist_impact === "number" && Number.isFinite(ev.dist_impact)) {
    return Math.abs(ev.dist_impact) * 100;
  }
  const a = ifOccursProbability(ev);
  if (a == null) return null;
  if (isProb(probability)) return Math.abs(a - probability) * 100;
  if (isProb(ev?.b)) return Math.abs(a - ev.b) * 100;
  return null;
}

/** "19 pts", "0.4 pts", or "—". */
export function fmtShift(pts) {
  if (pts == null || !Number.isFinite(pts)) return "—";
  if (pts > 0 && pts < 1) return `${pts.toFixed(1)} pts`;
  return `${Math.round(pts)} pts`;
}

/** "15%", "<1%", or "—". */
export function fmtChance(p) {
  if (!isProb(p)) return "—";
  const pct = p * 100;
  if (pct > 0 && pct < 1) return "<1%";
  if (pct < 100 && pct > 99) return ">99%";
  return `${Math.round(pct)}%`;
}

/**
 * Whether a trigger has settled, and how. A graded resolution (`resolutions[id].status`) wins;
 * otherwise a past trigger counts as having happened unless it is marked `occurred: false`.
 * Returns null while the trigger is still open.
 */
export function settledOutcome(ev, resolutions) {
  const status = resolutions?.[ev?.id]?.status;
  if (status === "occurred") return true;
  if (status === "did_not_occur") return false;
  if (ev?.isPast) return ev.occurred !== false;
  return null;
}

/**
 * The rows of the signposts table.
 *
 * @param {object[]} events      the forecast's triggers (as the lane draws them)
 * @param {object}   opts
 * @param {number}   opts.todayMs
 * @param {number}   [opts.resolutionMs]  the resolution moment; triggers are clamped to it
 * @param {number}   [opts.probability]   the yes/no forecast, for a binary trigger's shift
 * @param {object}   [opts.resolutions]   graded trigger outcomes keyed by id
 * @param {boolean}  [opts.concluded]     the question has settled: nothing is still upcoming
 * @returns {{ upcoming: object[], settled: object[] }}
 */
export function deriveSignposts(events, opts = {}) {
  const { todayMs = Date.now(), resolutionMs = NaN, probability = null, resolutions = null } = opts;
  const upcoming = [];
  const settled = [];
  const list = Array.isArray(events) ? events : [];
  for (let i = 0; i < list.length; i++) {
    const ev = list[i];
    if (!ev || typeof ev !== "object") continue;
    const startRaw = parseTriggerDate(ev.date);
    if (!Number.isFinite(startRaw)) continue;
    const endRaw = ev.date_end && ev.date_end !== ev.date ? parseTriggerDate(ev.date_end) : NaN;
    const outcome = settledOutcome(ev, resolutions);
    // A render key only. Occurrence keys spell the title, so Signposts.jsx keys its telemetry
    // (`data-ui-key`) by the trigger's id alone.
    const base = { key: eventKey(ev) || `trigger-${i}`, ev, title: ev.title || "" };
    if (outcome != null) {
      settled.push({
        ...base,
        occurred: outcome,
        atMs: startRaw,
        dateLabel: fmtSignpostDate(startRaw),
      });
      continue;
    }
    if (opts.concluded) continue;
    const capMs = Number.isFinite(resolutionMs) ? resolutionMs : Infinity;
    const isWindow = Number.isFinite(endRaw) && endRaw > startRaw;
    const startMs = Math.min(startRaw, capMs);
    const endMs = isWindow ? Math.min(endRaw, capMs) : startMs;
    // A trigger whose date has passed without an outcome says so rather than posing as upcoming.
    const lastDay = isWindow ? endRaw : startRaw;
    const subtitle =
      lastDay + DAY < todayMs
        ? `Was due ${fmtSignpostDate(lastDay)} · outcome not recorded`
        : isWindow
          ? startRaw <= todayMs
            ? `Window to ${fmtSignpostDate(endRaw)}`
            : fmtRange(startRaw, endRaw)
          : fmtSignpostDate(startRaw);
    upcoming.push({
      ...base,
      kind: isWindow ? "window" : "point",
      startMs,
      endMs,
      subtitle,
      chance: isProb(ev.p_event) ? ev.p_event : isProb(ev.probability) ? ev.probability : null,
      shift: signpostShift(ev, probability),
    });
  }
  upcoming.sort(
    (a, b) => (b.shift ?? -1) - (a.shift ?? -1) || a.startMs - b.startMs || a.endMs - b.endMs,
  );
  // Most recent first: what just happened is what the reader is most likely looking for.
  settled.sort((a, b) => b.atMs - a.atMs);
  return { upcoming, settled };
}

const MONTH_STEPS = [1, 2, 3, 6, 12, 24, 60];

/**
 * The shared axis: a domain from today to the resolution date (a little padding each side so
 * the today and resolution lines never sit on the track's edge), month-start ticks, and `pos`
 * mapping a moment to a 0–1 fraction of the track.
 */
export function signpostAxis({ todayMs, resolutionMs, rows = [], maxTicks = 6 }) {
  let start = todayMs;
  let end = Number.isFinite(resolutionMs)
    ? resolutionMs
    : rows.reduce((m, r) => Math.max(m, r.endMs ?? -Infinity), -Infinity);
  if (!Number.isFinite(end)) end = start + 365 * DAY;
  // A resolution already behind today (an overdue question) still needs a readable track.
  if (end - start < 14 * DAY) {
    const mid = Math.min(start, end);
    start = mid;
    end = Math.max(end, todayMs, mid + 14 * DAY);
  }
  const pad = (end - start) * 0.025;
  const d0 = start - pad;
  const d1 = end + pad;
  const pos = (ms) => Math.max(0, Math.min(1, (ms - d0) / (d1 - d0)));

  const first = new Date(d0);
  const spanMonths =
    (new Date(d1).getFullYear() - first.getFullYear()) * 12 +
    (new Date(d1).getMonth() - first.getMonth());
  const step = MONTH_STEPS.find((s) => spanMonths / s <= maxTicks) ?? 120;
  const ticks = [];
  let y = first.getFullYear();
  let m = first.getMonth() + 1;
  // Align to the step so ticks land on Jan/Jul (6), quarter starts (3), and so on.
  while ((y * 12 + m) % step !== 0) m += 1;
  for (let i = 0; i < 200; i++) {
    const t = new Date(y, m, 1).getTime();
    if (t > d1) break;
    if (t >= d0) {
      const d = new Date(t);
      ticks.push({
        ms: t,
        x: pos(t),
        label:
          step >= 12
            ? String(d.getFullYear())
            : d.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
      });
    }
    m += step;
  }
  return {
    d0,
    d1,
    pos,
    ticks,
    todayX: pos(todayMs),
    resX: Number.isFinite(resolutionMs) ? pos(resolutionMs) : null,
  };
}
